/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { color, mergeWithCss, xScreen } from "@rc/XDL/Utilities";
import { css } from "styled-components";

export const styledScrollbar = (width = 11, color?: string, thumbWidth = 3, thumbBorderColor = "transparent") => {
    return ({ theme }: any) => css`
        scrollbar-width: thin;
        scrollbar-color: ${color ? getColor(color) : resolveColor("common.ui4", theme)};
        overflow-y: auto;

        &::-webkit-scrollbar {
            width: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: #eeeeee;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${color ? getColor(color) : resolveColor("common.ui4", theme)};
            border-radius: 6px;
            border: ${thumbWidth}px solid ${getColor(thumbBorderColor)};
        }
    `;
};

export const styledScrollbarHorizontal = (width = 3, color?: string) => {
    return ({ theme }: any) => css`
        scrollbar-width: thin;
        scrollbar-color: ${color ? getColor(color) : resolveColor("common.ui8", theme)};
        overflow-x: auto;

        &::-webkit-scrollbar {
            height: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: transparent;
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${color ? getColor(color) : resolveColor("common.ui4", theme)};
            border-radius: 6px;
            border: 1px solid transparent;
        }
    `;
};

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

export const styledSlider = () => {
    return ({ theme }: any) => css`
        position: relative;
        .swiper-container {
            width: 100% !important;
        }
        .swiper-carousel {
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            z-index: 0;
        }
        .swiper-wrapper {
            box-sizing: border-box;
            z-index: 0;
        }

        .swiper-navigation {
            z-index: 1;
            > div {
                color: ${getColor("secondary")};
                > svg {
                    width: 30px;
                    height: 30px;
                }
            }
            .swiper-button-prev {
                z-index: 0;
                left: -25px;
                @media screen and (max-width: 1024px) {
                    left: -20px;
                }
                @media screen and (max-width: 768px) {
                    left: -5px;
                }
                &:after {
                    content: none;
                }
            }

            .swiper-button-next {
                z-index: 0;
                right: -25px;
                @media screen and (max-width: 1024px) {
                    right: -17px;
                }
                @media screen and (max-width: 768px) {
                    right: -5px;
                }
                &:after {
                    content: none;
                }
            }
        }

        .swiper-pagination-bullet {
            height: 19px;
            width: 19px;
            margin-right: 54px !important;
            &:last-child {
                margin-right: 54px !important;
            }
            background: ${getColor("common.ui3")};
            opacity: 1;
        }
        .swiper-pagination-bullet-active {
            background: ${getColor("primary.main")};
        }

        .swiper-button-disabled {
            display: none;
        }

        .swiper-slide {
            width: 100%;
            padding: 5px 15px;
            height: auto;
            min-height: 100%;

            @media (max-width: 768px) {
                display: flex;
                justify-content: center;
            }

            > div:first-child {
                height: 100%;

                @media (max-width: 768px) {
                    max-width: 310px;
                }
            }
        }
    `;
};

export const addScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelector("html")?.setAttribute("style", "scrollbar-gutter: stable;");
};

export const removeScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }
    document.querySelector("html")?.removeAttribute("style");
};

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [20] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const companyLogoItem: any = {
    css: css`
        display: none;
        padding: 0;
        width: 125px;
        float: left; // for print only

        @media print {
            display: block;
            padding-bottom: 20px;
            padding-left: 25px;
            margin-top: 5px;
        }

        img {
            display: block;
            max-width: 100%;
            height: auto;
        }
    `,
};

export const createLinkIconStyles = (
    icon: React.ComponentType | string,
    linkStyles?: LinkPresentationProps,
    isLarge = true,
    iconColor = "#444444",
) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "left" : "right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: isLarge ? 16 : 14,
                    weight: 500,
                    css: css`
                        &&& {
                            letter-spacing: 0;
                            line-height: 24px;
                            color: ${linkStyles?.color ? color(linkStyles?.color) : color("text.link")};
                            text-decoration: none;
                        }
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: 14,
                        color: iconColor,
                        css: isLarge
                            ? css`
                            &&& {
                                margin-${marginPosition}: 8px;
                                opacity: 0.66;
                                color: ${color("primary")};
                            }
                        `
                            : css`
                            &&& {
                                margin-${marginPosition}: 8px;
                                opacity: 0.66;
                                color: ${
                                    linkStyles?.color || iconColor
                                        ? color(iconColor || linkStyles?.color)
                                        : color("text.link")
                                };
                            }
                        `,
                    },
                    position: "left",
                },
            },
            linkStyles || {
                css: css`
                    &&& ${IconWrapper} {
                        color: ${iconColor};
                    }
                    &&& {
                        &:hover {
                            text-decoration: none;
                            * {
                                color: ${color("primary")};
                            }
                        }
                    }
                `,
            },
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
                margin-top: 10px;
                order: 1;
            }
        `,
    },
    submitButton: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};
